import {
    DAY_NAMES,
    MINUTES_PER_DAY,
    MINUTES_PER_WEEK,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_constants';
import {
    formatWeeklyTimeRange,
    getWeeklyRangeLabel,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_formatting';
import {
    type AvailabilityStatus,
    type RawWeeklyAvailability,
    type ScheduleDayGroup,
    type WeeklyAvailabilityEntry,
    type WeeklyAvailabilityEntrySegment,
    type WeeklyItem,
    type WeeklyScheduleRow,
    type WeeklyScheduleSegment,
    type WeeklySourceIdentity,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_types';
import {
    getAvailabilitySourceLabel,
    getCollapsedSourceLabel,
} from '@apps/web/components/caregivers/caregiver_availability_source_labels';

export function mergeWeeklyAvailability(
    weeklyAvailability: ReadonlyArray<RawWeeklyAvailability>,
    externalSourceLabel: string | undefined,
    referenceWeekStartKey: string,
    timezone: string,
): Array<WeeklyItem> {
    const entries = weeklyAvailability
        .map((item) => ({
            id: item.id,
            status: item.status,
            source: item.source,
            sourceLabel: getAvailabilitySourceLabel(
                item.source,
                item.source_system,
                externalSourceLabel,
            ),
            startMinuteOfWeek: item.start_minute_of_week,
            endMinuteOfWeek: item.end_minute_of_week,
        }))
        .sort(
            (a, b) =>
                a.startMinuteOfWeek - b.startMinuteOfWeek ||
                a.endMinuteOfWeek - b.endMinuteOfWeek,
        );

    const groups: Array<{
        status: AvailabilityStatus;
        startMinuteOfWeek: number;
        endMinuteOfWeek: number;
        entries: Array<WeeklyAvailabilityEntry>;
    }> = [];

    entries.forEach((entry) => {
        const current = groups[groups.length - 1];
        if (
            current &&
            current.status === entry.status &&
            entry.startMinuteOfWeek <= current.endMinuteOfWeek
        ) {
            current.endMinuteOfWeek = Math.max(
                current.endMinuteOfWeek,
                entry.endMinuteOfWeek,
            );
            current.entries.push(entry);
            return;
        }

        groups.push({
            status: entry.status,
            startMinuteOfWeek: entry.startMinuteOfWeek,
            endMinuteOfWeek: entry.endMinuteOfWeek,
            entries: [entry],
        });
    });

    if (groups.length > 1) {
        const first = groups[0]!;
        const last = groups[groups.length - 1]!;
        if (
            first.startMinuteOfWeek === 0 &&
            last.endMinuteOfWeek === MINUTES_PER_WEEK &&
            first.status === last.status
        ) {
            const combined = {
                status: last.status,
                startMinuteOfWeek: last.startMinuteOfWeek,
                endMinuteOfWeek: MINUTES_PER_WEEK + first.endMinuteOfWeek,
                entries: [...last.entries, ...first.entries],
            };
            groups.splice(groups.length - 1, 1);
            groups.shift();
            groups.push(combined);
        }
    }

    return groups.map((group) =>
        buildWeeklyItem(
            group.entries,
            group.startMinuteOfWeek,
            group.endMinuteOfWeek,
            referenceWeekStartKey,
            timezone,
        ),
    );
}

export function buildWeeklyDayGroups(
    items: ReadonlyArray<WeeklyItem>,
    weekStartDateKey: string,
    timezone: string,
): Array<ScheduleDayGroup<WeeklyScheduleRow>> {
    const rawSegments = items
        .flatMap((item) =>
            buildWeeklySegments({
                item,
                weekStartDateKey,
                timezone,
            }),
        )
        .sort((a, b) => a.sortMinuteOfWeek - b.sortMinuteOfWeek);
    const rows = compactWeeklySegments(withWeeklyConflictState(rawSegments));

    const groups: Array<ScheduleDayGroup<WeeklyScheduleRow>> = [];
    rows.forEach((row) => {
        const key = row.dayRange.label;
        const current = groups[groups.length - 1];
        if (current?.key === key) {
            current.items.push(row);
            return;
        }
        groups.push({
            key,
            label: row.dayRange.label,
            items: [row],
        });
    });

    return groups;
}

function buildWeeklyItem(
    entries: ReadonlyArray<WeeklyAvailabilityEntry>,
    startMinuteOfWeek: number,
    endMinuteOfWeek: number,
    referenceWeekStartKey: string,
    timezone: string,
): WeeklyItem {
    const labels = getWeeklyRangeLabel(
        startMinuteOfWeek,
        endMinuteOfWeek,
        referenceWeekStartKey,
        timezone,
    );
    return {
        id: `weekly-${entries.map((entry) => entry.id).join('-')}`,
        status: entries[0]?.status ?? 'available',
        sourceLabel: getCollapsedSourceLabel(entries),
        hasConflict: hasAvailabilityStatusConflict(entries),
        startMinuteOfWeek,
        endMinuteOfWeek,
        dayLabel: labels.dayLabel,
        timeLabel: labels.timeLabel,
        entries,
    };
}

function hasAvailabilityStatusConflict(
    entries: ReadonlyArray<{ readonly status: AvailabilityStatus }>,
): boolean {
    return new Set(entries.map((entry) => entry.status)).size > 1;
}

function weeklyEntryOverlapsSegment(
    entry: WeeklyAvailabilityEntry,
    segmentStartMinuteOfWeek: number,
    segmentEndMinuteOfWeek: number,
): boolean {
    const ranges = [
        {
            start: entry.startMinuteOfWeek,
            end: entry.endMinuteOfWeek,
        },
        {
            start: entry.startMinuteOfWeek + MINUTES_PER_WEEK,
            end: entry.endMinuteOfWeek + MINUTES_PER_WEEK,
        },
    ];

    return ranges.some(
        (range) =>
            range.start < segmentEndMinuteOfWeek &&
            range.end > segmentStartMinuteOfWeek,
    );
}

function buildWeeklyEntrySegments({
    entries,
    segmentStartMinuteOfWeek,
    segmentEndMinuteOfWeek,
    weekStartDateKey,
    timezone,
}: {
    readonly entries: ReadonlyArray<WeeklyAvailabilityEntry>;
    readonly segmentStartMinuteOfWeek: number;
    readonly segmentEndMinuteOfWeek: number;
    readonly weekStartDateKey: string;
    readonly timezone: string;
}): Array<WeeklyAvailabilityEntrySegment> {
    return entries
        .map((entry) => {
            const ranges = [
                {
                    start: entry.startMinuteOfWeek,
                    end: entry.endMinuteOfWeek,
                },
                {
                    start: entry.startMinuteOfWeek + MINUTES_PER_WEEK,
                    end: entry.endMinuteOfWeek + MINUTES_PER_WEEK,
                },
            ];
            const overlap = ranges.find(
                (range) =>
                    range.start < segmentEndMinuteOfWeek &&
                    range.end > segmentStartMinuteOfWeek,
            );
            if (!overlap) {
                return null;
            }

            const startMinuteOfWeek = Math.max(
                overlap.start,
                segmentStartMinuteOfWeek,
            );
            const endMinuteOfWeek = Math.min(
                overlap.end,
                segmentEndMinuteOfWeek,
            );

            return {
                id: `${entry.id}-${startMinuteOfWeek}-${endMinuteOfWeek}`,
                entry,
                status: entry.status,
                source: entry.source,
                sourceLabel: entry.sourceLabel,
                dayLabel: getWeeklyDayLabel(startMinuteOfWeek),
                startMinuteOfWeek,
                endMinuteOfWeek,
                timeLabel: formatWeeklyTimeRange(
                    startMinuteOfWeek,
                    endMinuteOfWeek,
                    weekStartDateKey,
                    timezone,
                ),
            };
        })
        .filter(
            (segment): segment is WeeklyAvailabilityEntrySegment =>
                segment !== null,
        );
}

function buildWeeklySegments({
    item,
    weekStartDateKey,
    timezone,
}: {
    readonly item: WeeklyItem;
    readonly weekStartDateKey: string;
    readonly timezone: string;
}): Array<WeeklyScheduleSegment> {
    const segments: Array<WeeklyScheduleSegment> = [];
    let cursor = item.startMinuteOfWeek;

    while (cursor < item.endMinuteOfWeek) {
        const rawDayIndex = Math.floor(cursor / MINUTES_PER_DAY);
        const segmentEnd = Math.min(
            item.endMinuteOfWeek,
            (rawDayIndex + 1) * MINUTES_PER_DAY,
        );
        const dayIndex = ((rawDayIndex % 7) + 7) % 7;
        const entries = item.entries.filter((entry) =>
            weeklyEntryOverlapsSegment(entry, cursor, segmentEnd),
        );
        segments.push({
            id: `${weekStartDateKey}-${item.id}-${cursor}-${segmentEnd}`,
            parentId: item.id,
            status: item.status,
            sourceLabel: getCollapsedSourceLabel(entries),
            hasConflict: item.hasConflict,
            dayIndex,
            dayLabel: DAY_NAMES[dayIndex] || 'Monday',
            startMinuteOfWeek: cursor,
            endMinuteOfWeek: segmentEnd,
            timeLabel: formatWeeklyTimeRange(
                cursor,
                segmentEnd,
                weekStartDateKey,
                timezone,
            ),
            sortMinuteOfWeek:
                dayIndex * MINUTES_PER_DAY + (cursor % MINUTES_PER_DAY),
            entries,
            entrySegments: buildWeeklyEntrySegments({
                entries,
                segmentStartMinuteOfWeek: cursor,
                segmentEndMinuteOfWeek: segmentEnd,
                weekStartDateKey,
                timezone,
            }),
        });
        cursor = segmentEnd;
    }

    return segments;
}

function withWeeklyConflictState(
    segments: ReadonlyArray<WeeklyScheduleSegment>,
): Array<WeeklyScheduleSegment> {
    return segments.map((segment) => ({
        ...segment,
        hasConflict: segments.some(
            (other) =>
                other.id !== segment.id &&
                other.dayIndex === segment.dayIndex &&
                other.status !== segment.status &&
                weeklySegmentsOverlap(segment, other),
        ),
    }));
}

function weeklySegmentsOverlap(
    first: WeeklyScheduleSegment,
    second: WeeklyScheduleSegment,
): boolean {
    const firstRange = getWeeklySegmentMinuteOfDayRange(first);
    const secondRange = getWeeklySegmentMinuteOfDayRange(second);

    return (
        firstRange.start < secondRange.end && firstRange.end > secondRange.start
    );
}

function compactWeeklySegments(
    segments: ReadonlyArray<WeeklyScheduleSegment>,
): Array<WeeklyScheduleRow> {
    const patternGroups: Array<Array<WeeklyScheduleSegment>> = [];
    segments.forEach((segment) => {
        const existingGroup = patternGroups.find((group) =>
            weeklySegmentsShareCompactionPattern(group[0]!, segment),
        );
        if (existingGroup) {
            existingGroup.push(segment);
            return;
        }
        patternGroups.push([segment]);
    });

    const rows: Array<WeeklyScheduleRow> = [];
    patternGroups.forEach((patternSegments) => {
        const sortedSegments = [...patternSegments].sort(
            (a, b) => a.dayIndex - b.dayIndex,
        );
        let currentRun: Array<WeeklyScheduleSegment> = [];

        sortedSegments.forEach((segment) => {
            const previous = currentRun[currentRun.length - 1];
            if (previous && segment.dayIndex !== previous.dayIndex + 1) {
                rows.push(buildWeeklyScheduleRow(currentRun));
                currentRun = [];
            }
            currentRun.push(segment);
        });

        if (currentRun.length > 0) {
            rows.push(buildWeeklyScheduleRow(currentRun));
        }
    });

    return rows.sort(compareWeeklyScheduleRows);
}

function compareWeeklyScheduleRows(
    first: WeeklyScheduleRow,
    second: WeeklyScheduleRow,
): number {
    return (
        getWeeklyRowDisplayPriority(first) -
            getWeeklyRowDisplayPriority(second) ||
        first.sortMinuteOfWeek - second.sortMinuteOfWeek ||
        getWeeklyDayRangeEndMinuteOfWeek(first) -
            getWeeklyDayRangeEndMinuteOfWeek(second)
    );
}

function getWeeklyRowDisplayPriority(row: WeeklyScheduleRow): number {
    return row.dayRange.startDayIndex === row.dayRange.endDayIndex ? 1 : 0;
}

function weeklySegmentsShareCompactionPattern(
    first: WeeklyScheduleSegment,
    second: WeeklyScheduleSegment,
): boolean {
    return (
        first.status === second.status &&
        first.hasConflict === second.hasConflict &&
        getWeeklySegmentStartMinuteOfDay(first) ===
            getWeeklySegmentStartMinuteOfDay(second) &&
        getWeeklySegmentDuration(first) === getWeeklySegmentDuration(second) &&
        weeklySourceIdentitiesEqual(
            getWeeklySegmentSourceIdentities(first),
            getWeeklySegmentSourceIdentities(second),
        )
    );
}

function getWeeklySegmentStartMinuteOfDay(
    segment: WeeklyScheduleSegment,
): number {
    return segment.startMinuteOfWeek % MINUTES_PER_DAY;
}

function getWeeklySegmentMinuteOfDayRange(segment: WeeklyScheduleSegment): {
    readonly start: number;
    readonly end: number;
} {
    const start = getWeeklySegmentStartMinuteOfDay(segment);
    return {
        start,
        end: start + getWeeklySegmentDuration(segment),
    };
}

function getWeeklySegmentDuration(segment: WeeklyScheduleSegment): number {
    return segment.endMinuteOfWeek - segment.startMinuteOfWeek;
}

function getWeeklySegmentSourceIdentities(
    segment: WeeklyScheduleSegment,
): Array<WeeklySourceIdentity> {
    const identities: Array<WeeklySourceIdentity> = [];
    segment.entries.forEach((entry) => {
        const identity = {
            source: entry.source,
            sourceLabel: entry.sourceLabel,
        };
        if (
            !identities.some((existing) =>
                weeklySourceIdentityEqual(existing, identity),
            )
        ) {
            identities.push(identity);
        }
    });
    return identities.sort(compareWeeklySourceIdentity);
}

function compareWeeklySourceIdentity(
    first: WeeklySourceIdentity,
    second: WeeklySourceIdentity,
): number {
    return (
        first.source.localeCompare(second.source) ||
        (first.sourceLabel ?? '').localeCompare(second.sourceLabel ?? '')
    );
}

function weeklySourceIdentitiesEqual(
    first: ReadonlyArray<WeeklySourceIdentity>,
    second: ReadonlyArray<WeeklySourceIdentity>,
): boolean {
    return (
        first.length === second.length &&
        first.every(
            (identity, index) =>
                !!second[index] &&
                weeklySourceIdentityEqual(identity, second[index]),
        )
    );
}

function weeklySourceIdentityEqual(
    first: WeeklySourceIdentity,
    second: WeeklySourceIdentity,
): boolean {
    return (
        first.source === second.source &&
        first.sourceLabel === second.sourceLabel
    );
}

function buildWeeklyScheduleRow(
    segments: ReadonlyArray<WeeklyScheduleSegment>,
): WeeklyScheduleRow {
    const first = segments[0]!;
    const last = segments[segments.length - 1]!;
    const entries = getUniqueWeeklyEntries(
        segments.flatMap((segment) => segment.entries),
    );

    return {
        id: `weekly-pattern-${segments.map((segment) => segment.id).join('-')}`,
        status: first.status,
        sourceLabel: first.sourceLabel,
        hasConflict: first.hasConflict,
        dayRange: {
            startDayIndex: first.dayIndex,
            endDayIndex: last.dayIndex,
            label: getWeeklyDayRangeLabel(first.dayIndex, last.dayIndex),
        },
        timeLabel: first.timeLabel,
        sortMinuteOfWeek: first.sortMinuteOfWeek,
        entries,
        entrySegments: segments.flatMap((segment) => segment.entrySegments),
    };
}

function getUniqueWeeklyEntries(
    entries: ReadonlyArray<WeeklyAvailabilityEntry>,
): Array<WeeklyAvailabilityEntry> {
    const entriesById = new Map<string, WeeklyAvailabilityEntry>();
    entries.forEach((entry) => entriesById.set(entry.id, entry));
    return Array.from(entriesById.values()).sort(
        (a, b) => a.startMinuteOfWeek - b.startMinuteOfWeek,
    );
}

function getWeeklyDayLabel(startMinuteOfWeek: number): string {
    const dayIndex =
        ((Math.floor(startMinuteOfWeek / MINUTES_PER_DAY) % 7) + 7) % 7;
    return DAY_NAMES[dayIndex] || 'Monday';
}

function getWeeklyDayRangeLabel(
    startDayIndex: number,
    endDayIndex: number,
): string {
    if (startDayIndex === endDayIndex) {
        return DAY_NAMES[startDayIndex] || 'Monday';
    }
    return `${DAY_NAMES[startDayIndex] || 'Monday'} - ${
        DAY_NAMES[endDayIndex] || 'Sunday'
    }`;
}

function getWeeklyDayRangeEndMinuteOfWeek(row: WeeklyScheduleRow): number {
    return row.dayRange.endDayIndex * MINUTES_PER_DAY;
}
